import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Component, OnInit, Inject, ViewEncapsulation } from '@angular/core';
import { FormTemplatePropertyInfo } from '../../models/form-template-instances/form-template-property-info';
import { FormTemplateProperties } from '../../models/form-template-instances/form-template-properties';
import { CellClickEvent, CellCloseEvent, SelectableSettings } from '@progress/kendo-angular-grid';
import { Keys } from '@progress/kendo-angular-common';
import { FormField } from '../../models/form-builder/form-field.model';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FormTemplateInstancesConfig } from '../../models/form-template-instances/form-template-instances-config.model';
import { FieldDefinitionService } from '../../services/field-definition.service';
import { Form } from '../../models/form-builder/form.model';
import { FieldDefinition } from '../../models/form-builder/field-definition.model';
import { FormTemplateInstancesButtons } from '../../models/form-template-instances/form-template-instances-buttons';
import { FormTemplateInstancesButtonInfo } from '../../models/form-template-instances/form-template-instances-button-info';

// This is the dialog used to choose which FormFields will be used in a Home Page Grid
// On save, it outputs the data structure which will be written to the Home Page Grid Field's
// formTemplateInstancesGridJsonConfig property
@Component({
    selector: 'app-configure-home-page-grid-columns',
    templateUrl: './configure-home-page-grid-columns.dialog.html',
    styleUrls: ['./configure-home-page-grid-columns.dialog.scss'],
    standalone: false
})
export class ConfigureHomePageGridColumnsDialog implements OnInit {
    // availableProperties is the underlying data structure which gets modified in this dialog, converted to JSON and stored in the DB
    private availableProperties: FormTemplatePropertyInfo[];
    private formTemplateProperties: FormTemplateProperties = new FormTemplateProperties();
    private field: FormField;
    private selectedGridFieldId: number;
    private commandColumnPostion: string;
    private formTemplateInstancesButtons: FormTemplateInstancesButtons;

    private selectionSettings: SelectableSettings = {
        checkboxOnly: true,
        mode: 'multiple'
    }

    //private selectedPropertyNames: string[];
    private selectedFormTemplatePropertiesIndexes: number[] = null;
    private indexesOfSelectedGridFields: number[] = [];
    private indexesOfSelectedMetaDataAndFormLevelFields: number[] = [];
    private indexesOfSelectedActionButtons: number[] = [];
    private editingAliases: boolean;
    private form: Form = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) data,
        public dialogRef: MatDialogRef<ConfigureHomePageGridColumnsDialog>,
        private formBuilder: UntypedFormBuilder,
        private fieldDefinitionService: FieldDefinitionService
    ) {
        //***data.availableProperties contain all the columns that can displayed on Configure Form Grid pg
        this.availableProperties = data.availableProperties.filter(x => { return x.type != FormTemplatePropertyInfo.FIELD_WITH_ONLY_VIRTUAL_FIELDS });
        this.selectedFormTemplatePropertiesIndexes = data.selectedFormTemplatePropertiesIndexes;
        this.formTemplateProperties = data.formTemplateProperties;
        this.field = data.field;
        this.selectedGridFieldId = data.selectedGridFieldId;
        this.commandColumnPostion = data.commandColumnPostion;
        this.formTemplateInstancesButtons = data.formTemplateInstancesButtons;
        this.form = <Form>data.form;

        this.resetGridLevelFields();
        this.initializeSelectedIndexes();
    }

    ngOnInit(): void {
        let simpleFormGridButtons: string[] = FormTemplateInstancesButtons.simpleFormGridButtonNames;

        if (!this.field.enableSimpleFormGridDisplay) {
            this.availableProperties = this.availableProperties.filter(x => {
                return x.type == FormTemplatePropertyInfo.METADATA || x.type == FormTemplatePropertyInfo.FORM_LEVEL_FIELD || x.type == FormTemplatePropertyInfo.GRID_FIELD
            });

            //***Reset Action button list*/
            if (this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo.length <= simpleFormGridButtons.length) {
                this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo = null;
                this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo = this.ActionButtons;
            }

        } else {
            /***Only use default properties if simple grid option is enabled*/
            this.availableProperties = this.MetaDataFields;

            //***Only display Simple buttons */
            //***Confirm with Paul that it is ok that I added a setter to this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo*/
            //***Other option (much more work) is to create a new instance of this.formTemplateInstancesButtons and assign all the old values to it and include only filtered btns */
            this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo = this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo.filter(x => simpleFormGridButtons.includes(x.buttonDisplayName));
        }
    }

    public get SelectedGridFieldId() {
        return this.selectedGridFieldId;
    }
    public set SelectedGridFieldId(value: number) {
        this.selectedGridFieldId = value;
    }

    public get CommandColumnPostion(): string {
        return this.commandColumnPostion;
    }
    public set CommandColumnPostion(value: string) {
        this.commandColumnPostion = value;
    }

    public get FormTemplateButtonInfo(): FormTemplateInstancesButtonInfo[] {
        this.formTemplateInstancesButtons.createDefaultButtonInfoIfNoConfigExists();

        return this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo;
    }

    public get AllSelectableFields() {
        let fields = this.availableProperties.filter(x => { return x.type == FormTemplatePropertyInfo.METADATA || x.type == FormTemplatePropertyInfo.FORM_LEVEL_FIELD });
        return fields;
    }

    // Helper method which retuns only those fields in availableProperties which are meta data fields
    public get MetaDataFields() {
        let fields = this.availableProperties.filter(x => { return x.type == FormTemplatePropertyInfo.METADATA });
        return fields;
    }

    // Helper method which retuns only those fields in availableProperties which are top level form fields
    public get ToplevelFormFields() {
        let fields = this.availableProperties.filter(x => { return x.type == FormTemplatePropertyInfo.FORM_LEVEL_FIELD });
        return fields;
    }

    // Helper method which retuns only those fields in availableProperties which are Child fields of Grid fields
    // Returns null if no Grid field has been selected
    public get GridlevelFormFields() {
        if (this.selectedGridFieldId) {
            let field = this.availableProperties.filter(x => { return x.fieldId == this.selectedGridFieldId })[0];
            return field?.children;
        } else {
            return null;
        }
    }

    public get ActionButtons(): FormTemplateInstancesButtonInfo[] {
        this.formTemplateInstancesButtons.createDefaultButtonInfoIfNoConfigExists();
        return this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo;
    }

    public get Grids() {
        let fields = this.availableProperties.filter(x => { return x.type == FormTemplatePropertyInfo.GRID_FIELD });
        return fields;
    }

    public get FormTemplateProperties() {
        return this.formTemplateProperties;
    }

    public get EditingFormTemplatePropertiesAliases() {
        return true;
    }

    public get SelectedGridProperties() {
        let selectedGridFIeld = this.Grids.filter(x => x.fieldId == this.selectedGridFieldId)[0];
        if (selectedGridFIeld) {
            let childFields = selectedGridFIeld.children;
            return childFields;
        } else {
            return [];
        }
    }

    public closeDialog(save: boolean = false) {
        if (save) {
            // Loop through all the meta and form fields and for any that are selected set the field's isSelected property
            //***Consider using the propertyName instead of just the index location*/
            for (let i = 0; i < this.AllSelectableFields.length; i++) {
                let field = this.AllSelectableFields[i];
                if (this.indexesOfSelectedMetaDataAndFormLevelFields.indexOf(i) > -1) {
                    field.isSelected = true;
                } else {
                    field.isSelected = false;
                }
            }

            // Do the same for Grid-level fields
            if (this.GridlevelFormFields) {
                for (let i = 0; i < this.GridlevelFormFields.length; i++) {
                    let child = this.GridlevelFormFields[i];
                    if (this.indexesOfSelectedGridFields.indexOf(i) > -1) {
                        child.isSelected = true;
                    } else {
                        child.isSelected = false;
                    }
                    // for child fields, push them onto the available properties to make them easier to deal with for other code
                    let exists = this.availableProperties.filter(x => { return x.fieldId == child.fieldId });
                    if (exists.length == 0) {
                        this.availableProperties.push(child);
                    }
                }
            } else {
                this.resetGridLevelFields();
            }

            // Loop though action buttons and make sure they are marked as selected/not selected
            for (let i = 0; i < this.ActionButtons.length; i++) {
                let btnInfo = this.ActionButtons[i];
                if (this.indexesOfSelectedActionButtons.indexOf(i) > -1) {
                    btnInfo.enableButton = true;
                } else {
                    btnInfo.enableButton = false;
                }
            }
            let templateInstancesConfig: FormTemplateInstancesConfig = new FormTemplateInstancesConfig(this.availableProperties, this.selectedGridFieldId, this.commandColumnPostion);

            this.dialogRef.close({ templateInstancesConfig: templateInstancesConfig, actionButtonsConfig: this.formTemplateInstancesButtons });
        } else {
            this.dialogRef.close();
        }
    }

    // puts the clicked cell into edit mode so its value can be edited
    public handleCellClick(event: any) {
        const ALIAS_CELL_COLUMN_INDEX = 2;
        const COLUMN_WIDTH_COLUMN_INDEX = 3;
        const OPTIONAL_FILTER_VALUE_COLUMN_INDEX = 4;
        const OPTIONAL_AGGREGATE_NAMES_COLUMN_INDEX = 5;

        let columnIndex: number = event.columnIndex;
        let fg: UntypedFormGroup = null;
        //if (columnIndex == ALIAS_CELL_COLUMN_INDEX) {
        if (event.column.field == "alias") {
            fg = this.formBuilder.group({
                alias: event.dataItem.alias
            });
        } else if (columnIndex == COLUMN_WIDTH_COLUMN_INDEX) {
            fg = this.formBuilder.group({
                columnWidth: event.dataItem.columnWidth > 0 ? event.dataItem.columnWidth : 0
            });
        //} else if (columnIndex == OPTIONAL_FILTER_VALUE_COLUMN_INDEX) {
        } else if (event.column.field == "optionalFilterValue") {
            fg = this.formBuilder.group({
                optionalFilterValue: event.dataItem.optionalFilterValue
            });
        //} else if (columnIndex == OPTIONAL_AGGREGATE_NAMES_COLUMN_INDEX) {
        } else if (event.column.field == "optionalAggregateNames") {
            fg = this.formBuilder.group({
                optionalAggregateNames: event.dataItem.optionalAggregateNames
            });
        } else if (event.column.field == "associatedFlexFieldName") {
            
            fg = this.formBuilder.group({
                associatedFlexFieldName: event.dataItem.associatedFlexFieldName
            });
        }
        if (!event.isEdited) {
            this.editingAliases = true;
            event.sender.editCell(event.rowIndex, columnIndex, fg);
        }
    }

    public handleActionButtonCellClick(event: any) {
        if (event.columnIndex == 1) {
            let fg: UntypedFormGroup = this.formBuilder.group({
                alias: event.dataItem.alias
            });
            event.sender.editCell(event.rowIndex, event.columnIndex, fg);
        }
    }
    public handleActionButtonCellChange(args: CellCloseEvent): void {
        const { formGroup, dataItem } = args;
        let alias = formGroup.value.alias;
        args.sender.data['data'][args.rowIndex].alias = alias;
    }

    // applies a given alias to appropriate object in the underlying data structure
    public handlePropertiesChange(args: CellCloseEvent): void {
        const { formGroup, dataItem } = args;

        if (!formGroup.valid) {
            // prevent closing the edited cell if there are invalid values.
            args.preventDefault();
        } else if (formGroup.dirty) {
            if (args.originalEvent && args.originalEvent.keyCode === Keys.Escape) {
                return;
            }

            if (args.column.field == 'alias') {
                let alias = formGroup.value.alias;
                args.sender.data['data'][args.rowIndex].alias = alias;
            }
            else if (args.column.field == 'columnWidth') {
                let columnWidth = isNaN(formGroup.value.columnWidth) ? 0 : Number(formGroup.value.columnWidth);
                args.sender.data['data'][args.rowIndex].columnWidth = columnWidth;
            }
            else if (args.column.field == 'optionalFilterValue') {
                let optionalFilterValue = formGroup.value.optionalFilterValue;
                args.sender.data['data'][args.rowIndex].optionalFilterValue = optionalFilterValue;

                let propertyInfo: FormTemplatePropertyInfo = <FormTemplatePropertyInfo>args.sender.data['data'][args.rowIndex];
                if ((propertyInfo.defaultFilterOperator == null) && (propertyInfo.type == 'field') && (propertyInfo.fieldId != null) && (this.form != null)) {
                    let formField: FormField = this.form.formFields.find(ff => ff.id == args.sender.data['data'][args.rowIndex].fieldId)
                    if (formField != null) {
                        let fieldDefClientLogic = this.fieldDefinitionService.getFieldClientLogicHandler(formField.fieldDefinitionClassName);
                        propertyInfo.defaultFilterOperator = fieldDefClientLogic.getDefaultKendoGridOperatorName();
                    }
                }
            }
            else if (args.column.field == 'optionalAggregateNames') {
                let optionalAggregateNames = formGroup.value.optionalAggregateNames;
                args.sender.data['data'][args.rowIndex].optionalAggregateNames = optionalAggregateNames;

                let propertyInfo: FormTemplatePropertyInfo = <FormTemplatePropertyInfo>args.sender.data['data'][args.rowIndex];
                if ((propertyInfo.type == 'field') && (propertyInfo.fieldId != null) && (this.form != null)) {
                    let formField: FormField = this.form.formFields.find(ff => ff.id == args.sender.data['data'][args.rowIndex].fieldId);
                    if (formField != null) {
                        let fieldDefinition: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(formField.fieldDefinitionClassName);
                        if (!fieldDefinition.isNumeric) {
                            // Only numeric fields can have aggregators.
                            propertyInfo.optionalAggregateNames = null;
                        }
                    }
                }
            } else if (args.column.field == 'associatedFlexFieldName') {
                //**Get the updated internal name for the flex field
                let associatedFlexFieldName = formGroup.value.associatedFlexFieldName;
                args.sender.data['data'][args.rowIndex].associatedFlexFieldName = associatedFlexFieldName;
            }
        }

        let field = null;
        if (dataItem.type == FormTemplatePropertyInfo.METADATA) {
            field = this.MetaDataFields.filter(x => x.propertyName == dataItem.propertyName)[0];
        } else if (dataItem.type == FormTemplatePropertyInfo.FORM_LEVEL_FIELD) {
            field = this.ToplevelFormFields.filter(x => x.propertyName == dataItem.propertyName)[0];
        } else if (dataItem.type == FormTemplatePropertyInfo.CHILD_FIELD_OF_GRID && this.GridlevelFormFields) {
            field = this.GridlevelFormFields.filter(x => x.propertyName == dataItem.propertyName)[0];
        }
        
        if (field != null) {
            if (args.column.field == 'alias')
                field.alias = dataItem.alias;
            else if (args.column.field == 'columnWidth')
                field.columnWidth = dataItem.columnWidth;
            else if (args.column.field == 'optionalFilterValue')
                field.optionalFilterValue = dataItem.optionalFilterValue;
            else if (args.column.field == 'associatedFlexFieldName')
                field.associatedFlexFieldName = dataItem.associatedFlexFieldName;
        }
    }

    // The order of columns is based purely on the order in which properties are saved into a Field's FormTemplateInstancesGridJsonConfig property
    // While metadata and form-level fields can be intermingled, grid-level fields cannot and are always positioned at the end
    public handleRowReorder(event: any) {
        let dataItem = event.dropTargetRow.dataItem;
        let offset = this.offset(dataItem);
        let originalIndexOfMovedRow = event.draggedRows[0].rowIndex + offset;
        let originalIndexOfTargetRow = event.dropTargetRow.rowIndex + offset;

        let dropPosition = event.dropPosition;
        let newIndex = dropPosition == 'before' ? originalIndexOfTargetRow : (originalIndexOfTargetRow + 1);

        let properties = this.availableProperties.filter(x => { return x.type != FormTemplatePropertyInfo.GRID_FIELD })

        let movedRow = properties.splice(originalIndexOfMovedRow, 1)[0]; // first remove the moved Row but keep a reference to it so it can be reinserted
        let before = properties.slice(0, newIndex); // grab everything before its new location in the array
        let after = properties.slice(newIndex); // grab everything after its new location in the array

        //console.log('movedRow');
        //console.log(movedRow);
        //console.log('before');
        //console.log(before);
        //console.log('after');
        //console.log(after);

        // reorder the available properties
        this.availableProperties = [
            ...before,
            movedRow,
            ...after,
            ...this.availableProperties.filter(x => { return x.type == FormTemplatePropertyInfo.GRID_FIELD }) // always position grid-level fields after everything else
        ];

        this.initializeSelectedIndexes();
    }

    // If the dataItem is for a field in a grid, return an offset of the number of none-grid fields
    // otherwise return 0. This is for re-ordering rows, where grid fields always get stored after
    // other fields
    private offset(dataItem: FormTemplatePropertyInfo) {
        if (dataItem.type == FormTemplatePropertyInfo.CHILD_FIELD_OF_GRID) {
            return this.MetaDataFields.length + this.ToplevelFormFields.length;
        } else {
            return 0;
        }
    }

    public handleGridSelection(event: any) {
        this.availableProperties = this.availableProperties.filter(x => { return x.type != FormTemplatePropertyInfo.CHILD_FIELD_OF_GRID });
    }

    public doneAliasing() { }

    public get SelectionSettings() {
        return this.selectionSettings;
    }

    public get SelectedFormTemplateProperties(): number[] {
        return this.selectedFormTemplatePropertiesIndexes;
    }

    public set SelectedFormTemplateProperties(selectedIndexes: number[]) {
        this.selectedFormTemplatePropertiesIndexes = selectedIndexes;

        for (let i = 0; i < this.formTemplateProperties.availableProperties.length; i++) {
            this.formTemplateProperties.availableProperties[i].isSelected = (selectedIndexes.indexOf(i) > -1);
        }

        this.setPropertiesOnField();
    }

    public get IndexesOfSelectedMetaDataAndFormLevelFields(): number[] {
        return this.indexesOfSelectedMetaDataAndFormLevelFields;
    }
    public set IndexesOfSelectedMetaDataAndFormLevelFields(selectedIndexes: number[]) {
        this.indexesOfSelectedMetaDataAndFormLevelFields = selectedIndexes;
    }
    public get IndexesOfSelectedGridFields(): number[] {
        return this.indexesOfSelectedGridFields;
    }
    public set IndexesOfSelectedGridFields(selectedIndexes: number[]) {
        this.indexesOfSelectedGridFields = selectedIndexes;
    }
    public get IndexesOfSelectedActionButtons(): number[] {
        return this.indexesOfSelectedActionButtons;
    }
    public set IndexesOfSelectedActionButtons(selectedIndexes: number[]) {
        this.indexesOfSelectedActionButtons = selectedIndexes;
    }

    private setPropertiesOnField(): void {
        // Make this type safe
        let obj = {
            formTemplateProperties: this.formTemplateProperties.availableProperties
        };
        let formTemplateInstancesConfig: FormTemplateInstancesConfig = new FormTemplateInstancesConfig(this.formTemplateProperties.availableProperties);

        this.field.formTemplateInstancesGridJsonConfig = JSON.stringify(obj);
        let configJson: string = formTemplateInstancesConfig.toJsonString();

        if (this.field.formTemplateInstancesGridJsonConfig != configJson)
            throw 'ConfigureHomePageGridColumnsDialog.setPropertiesOnField():  the configuration strings do not match';

        this.field.formTemplateInstancesGridJsonConfig = configJson;
    }

    // Populates the indexesOfSelectedMetaDataAndFormLevelFields, indexesOfSelectedGridFields and indexesOfSelectedActionButtons
    // Arrays which the Kendo Grids on the template use to display checkboxes as checked/unchecked
    private initializeSelectedIndexes() {
        this.indexesOfSelectedMetaDataAndFormLevelFields = [];
        this.indexesOfSelectedGridFields = [];

        for (let i = 0; i < this.AllSelectableFields.length; i++) {
            let field = this.AllSelectableFields[i];
            if (field.isSelected) {
                this.indexesOfSelectedMetaDataAndFormLevelFields.push(i);
            }
        }

        if (this.GridlevelFormFields) {
            for (let i = 0; i < this.GridlevelFormFields.length; i++) {
                let field = this.GridlevelFormFields[i];
                if (field.isSelected) {
                    this.indexesOfSelectedGridFields.push(i);
                }
            }
        }

        //***Reset Action button list*/
        //debugger;
        //let simpleFormGridButtons: string[] = FormTemplateInstancesButtons.simpleFormGridButtonNames;
        //let currBtnSelections = this.FormTemplateButtonInfo;

        //if (this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo.length <= simpleFormGridButtons.length) {
        //    this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo = null;
        //    this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo = this.ActionButtons;
        //}

        //for (let i = 0; i < this.FormTemplateButtonInfo.length; i++) {
        //    let btnInfo = this.FormTemplateButtonInfo[i];
        //    let tmp = currBtnSelections[i];

        //    //***iterate currBtn, find item in FormTemplaetButtonIno and set its property to true. all others should be false; may need to use current coll to iterate since we're setting its value*/
        //    if (currBtnSelections[i] != null && currBtnSelections[i].buttonDisplayName == btnInfo.buttonDisplayName && currBtnSelections[i].enableButton) {
        //        this.indexesOfSelectedActionButtons.push(i);
        //    }
        //}

        for (let i = 0; i < this.FormTemplateButtonInfo.length; i++) {
            let btnInfo = this.FormTemplateButtonInfo[i];
            if (btnInfo.enableButton) {
                this.indexesOfSelectedActionButtons.push(i);
            }
        }
    }

    // 1) Resets any Grid-Level fields so we're sure to only have those belonging to a selected Grid. (And none, if no Grid have been selected)
    //    (This is just to ensure that the JSON representing available and selected columns is kept clean.)
    // 2) Moves any Grid fields onto the end of this.availableProperties which keeps the sorting of columns simple
    private resetGridLevelFields() {
        let indexesOfChildFields = [];
        for (let i = 0; i < this.availableProperties.length; i++) {
            let prop = this.availableProperties[i];
            if (prop.type == FormTemplatePropertyInfo.CHILD_FIELD_OF_GRID) {
                indexesOfChildFields.push(i);
            }
        }
        for (let i of indexesOfChildFields.reverse()) {
            this.availableProperties.splice(i, 1);
        }

        if (this.selectedGridFieldId > 0) {
            if (this.GridlevelFormFields?.length > 0) {
                for (let i = 0; i < this.GridlevelFormFields?.length; i++) {
                    let child = this.GridlevelFormFields[i];
                    this.availableProperties.push(child);
                }
            } else {
                this.selectedGridFieldId = null;
            }
        }

        if (this.selectedGridFieldId == 0) this.selectedGridFieldId = null;

        this.availableProperties = [
            ...this.availableProperties.filter(x => { return x.type != FormTemplatePropertyInfo.GRID_FIELD }),
            ...this.availableProperties.filter(x => { return x.type == FormTemplatePropertyInfo.GRID_FIELD })
        ];
    }
}
